'use client'

import { ProductsConnectionEdge } from '@/eshop/gql/graphql'
import clsx from 'clsx'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import EshopSearch from '../Search'
import EshopDocumentsSection from './DocumentsSection'
import EshopBatchSection from './BatchSection'
import { Heading } from '@/components/Heading'
import useResponsive from '@/hooks/useResponsive'

interface Props {
    initialLetter: string
    initialProducts: ProductsConnectionEdge[]
}

export default function EshopDocuments({
    initialLetter,
    initialProducts,
}: Props) {
    const [letter, setLetter] = useState(initialLetter || 'A')
    const [batch, setBatch] = useState<string>('')

    const { t } = useTranslation()

    const handleLetterChange = useCallback((l: string | null) => {
        !!l && setLetter(l)
    }, [])

    return (
        <div>
            <Heading
                tag="h2"
                text={t('eshop.documents.heading')}
                className="mb-10"
            />
            <EshopSearch
                placeholder={t('eshop.documents.searchPlaceholder')}
                onSearch={setBatch}
                debounce={500}
                className="my-6"
            />
            {!batch && (
                <>
                    <EshopDocumentsLetters
                        onChange={handleLetterChange}
                        activeLetter={letter}
                    />
                    <hr className="my-6" />
                </>
            )}
            {!batch && (
                <EshopDocumentsSection
                    initialProducts={initialProducts}
                    letter={letter}
                />
            )}
            {!!batch && <EshopBatchSection search={batch} />}
        </div>
    )
}

const LETTERS = 'A|B|C|D|E|F|G|H|Ch|I|J|K|L|M|N|O|P|Q|R|S|T|U|V|W|X|Y|Z|0-#'
interface LettersProps {
    activeLetter: string | null
    onChange: (letter: string | null) => void
}

export function EshopDocumentsLetters({
    activeLetter,
    onChange,
}: LettersProps) {
    const { isDesktop } = useResponsive()

    const handleToggleLetter = useCallback(
        (l: string) => () => {
            onChange(l)
        },
        [onChange]
    )
    return (
        <div
            className={clsx(
                'flex gap-6 px-4 text-sm text-blue-middle',
                isDesktop ? 'justify-between' : 'flex-wrap'
            )}
        >
            {LETTERS.split('|').map((letter) => (
                <span
                    key={letter}
                    className={clsx(
                        'cursor-pointer',
                        letter === '0-#' && 'text-[1rem]',
                        letter === activeLetter && 'font-bold'
                    )}
                    onClick={handleToggleLetter(letter)}
                >
                    {letter}
                </span>
            ))}
        </div>
    )
}
